import { CompleteKundali, Language, PlanetName, ZodiacSign, KundaliChartData } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { VargaConvergenceAnalysis, VargaSignal } from '../../types/v518';
import { ZODIAC_LORDS } from '../vedicEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.18 — CROSS-VARGA CONVERGENCE ENGINE (V518-02)
   Corroborates Natal Indicators Across Dedicated Divisional Charts (Vargas)
   ========================================================================== */

const THEME_VARGA_ROUTING: Record<LifeThemeId, {
  vargas: ('D1' | 'D2' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12' | 'D20')[];
  primaryHouse: number;
  secondaryHouse: number;
  themeNameEn: string;
  themeNameHi: string;
}> = {
  career_job: {
    vargas: ['D1', 'D10', 'D9'],
    primaryHouse: 10,
    secondaryHouse: 6,
    themeNameEn: 'Career & Professional Enterprise',
    themeNameHi: 'करियर एवं व्यावसायिक उद्यम'
  },
  marriage_relationship: {
    vargas: ['D1', 'D9'],
    primaryHouse: 7,
    secondaryHouse: 2,
    themeNameEn: 'Marriage & Partnerships',
    themeNameHi: 'विवाह एवं साझेदारी'
  },
  wealth_finance: {
    vargas: ['D1', 'D4', 'D2'],
    primaryHouse: 2,
    secondaryHouse: 11,
    themeNameEn: 'Wealth & Financial Growth',
    themeNameHi: 'धन एवं वित्तीय समृद्धि'
  },
  house_property: {
    vargas: ['D1', 'D4'],
    primaryHouse: 4,
    secondaryHouse: 11,
    themeNameEn: 'Real Estate & Fixed Assets',
    themeNameHi: 'भूमि, भवन एवं अचल संपत्ति'
  },
  vehicle: {
    vargas: ['D1', 'D4'],
    primaryHouse: 4,
    secondaryHouse: 12,
    themeNameEn: 'Vehicles & Conveyances',
    themeNameHi: 'वाहन एवं सुख साधन'
  },
  children: {
    vargas: ['D1', 'D7', 'D9'],
    primaryHouse: 5,
    secondaryHouse: 9,
    themeNameEn: 'Progeny & Family Lineage',
    themeNameHi: 'संतान एवं कुल वृद्धि'
  },
  education: {
    vargas: ['D1', 'D4', 'D9'],
    primaryHouse: 5,
    secondaryHouse: 4,
    themeNameEn: 'Higher Education & Academic Success',
    themeNameHi: 'उच्च शिक्षा एवं अकादमिक सफलता'
  },
  health_vitality: {
    vargas: ['D1', 'D9'],
    primaryHouse: 1,
    secondaryHouse: 6,
    themeNameEn: 'Health, Vitality & Longevity',
    themeNameHi: 'स्वास्थ्य, जीवन शक्ति एवं दीर्घायु'
  },
  business: {
    vargas: ['D1', 'D10', 'D7'],
    primaryHouse: 7,
    secondaryHouse: 10,
    themeNameEn: 'Business & Commercial Ventures',
    themeNameHi: 'व्यापार एवं स्वतंत्र व्यवसाय'
  },
  foreign_travel: {
    vargas: ['D1', 'D9', 'D12'],
    primaryHouse: 12,
    secondaryHouse: 9,
    themeNameEn: 'Foreign Settlement & Global Travel',
    themeNameHi: 'विदेश गमन एवं अंतर्राष्ट्रीय यात्रा'
  },
  litigation_competition: {
    vargas: ['D1', 'D10', 'D9'],
    primaryHouse: 6,
    secondaryHouse: 10,
    themeNameEn: 'Competition, Legal Matters & Victory',
    themeNameHi: 'प्रतियोगिता, कानूनी मामले एवं विजय'
  },
  spirituality: {
    vargas: ['D1', 'D9', 'D20'],
    primaryHouse: 9,
    secondaryHouse: 12,
    themeNameEn: 'Spiritual Awakening & Dharma',
    themeNameHi: 'आध्यात्मिक जागृति एवं धर्म'
  },
  family_parents: {
    vargas: ['D1', 'D4', 'D12'],
    primaryHouse: 4,
    secondaryHouse: 9,
    themeNameEn: 'Family Welfare & Parental Harmony',
    themeNameHi: 'पारिवारिक सुख एवं माता-पिता'
  }
};

const VARGA_NAMES_EN: Record<string, string> = {
  D1: 'Rashi (Physical Reality & Natal Foundation)',
  D9: 'Navamsha (Soul Fruit, Destiny & Inner Strength)',
  D10: 'Dashamsha (Professional Achievement & Executive Status)',
  D7: 'Saptamsha (Children & Progeny Blessings)',
  D4: 'Chaturthamsha (Fixed Assets, Fortune & Property)',
  D2: 'Hora (Liquid Wealth & Resources)',
  D20: 'Vimsamsha (Spiritual Sadhana & Divine Grace)',
  D12: 'Dwadamsha (Ancestral Heritage & Parental Lineage)'
};

const VARGA_NAMES_HI: Record<string, string> = {
  D1: 'राशि चक्र (भौतिक आधार एवं जन्म स्थिति)',
  D9: 'नवांश (भाग्य, आंतरिक शक्ति एवं जीवन साथी)',
  D10: 'दशांश (व्यावसायिक प्रतिष्ठा एवं कर्म क्षेत्र)',
  D7: 'सप्तांश (संतान एवं कुल सुख)',
  D4: 'चतुर्थांश (अचल संपत्ति, भाग्य एवं भवन)',
  D2: 'होरा (तरल धन एवं संपदा)',
  D20: 'विंशांश (आध्यात्मिक साधना एवं ईष्ट कृपा)',
  D12: 'द्वादशांश (माता-पिता एवं पैतृक प्रभाव)'
};

const BENEFIC_GRAHAS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];

/**
 * Extracts divisional chart details and calculates the signal
 */
function evaluateVargaSignal(
  kundali: CompleteKundali,
  vargaCode: 'D1' | 'D2' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12' | 'D20',
  houseNumber: number,
  isHi: boolean
): VargaSignal {
  const chartData: KundaliChartData | undefined = kundali.charts[vargaCode];
  
  // Fallback if chart not found
  if (!chartData || !chartData.houses || chartData.houses.length < 12) {
    const d1House = kundali.houses[houseNumber - 1] || kundali.houses[0];
    const lord = (ZODIAC_LORDS[d1House.sign] as PlanetName) || 'Jupiter';
    return {
      vargaCode,
      vargaNameEn: VARGA_NAMES_EN[vargaCode] || vargaCode,
      vargaNameHi: VARGA_NAMES_HI[vargaCode] || vargaCode,
      relevantHouse: houseNumber,
      houseSign: d1House.sign,
      houseLord: lord,
      lordDignity: 'Neutral',
      occupantPlanets: d1House.planetsInHouse || [],
      signalStrength: 'NEUTRAL',
      pointsContribution: 0,
      rationaleEn: `${vargaCode} confirms stable foundational indications for House ${houseNumber}.`,
      rationaleHi: `${vargaCode} भाव ${houseNumber} के लिए स्थिर आधारभूत संकेत प्रस्तुत करता है।`
    };
  }

  const targetHouse = chartData.houses[houseNumber - 1] || chartData.houses[0];
  const houseSign = targetHouse.sign || 'Aries';
  const houseLord = (ZODIAC_LORDS[houseSign] as PlanetName) || 'Jupiter';
  const occupantPlanets = targetHouse.planetsInHouse || [];

  // Check dignity of lord in this varga
  const lordPlanet = chartData.planets?.find(p => p.name === houseLord);
  const lordDignity = lordPlanet?.dignity || 'Neutral';

  // Evaluate benefics and malefics
  const beneficsInHouse = occupantPlanets.filter(p => BENEFIC_GRAHAS.includes(p));
  const maleficsInHouse = occupantPlanets.filter(p => !BENEFIC_GRAHAS.includes(p));

  let pointsContribution = 0;
  if (lordDignity === 'Exalted' || lordDignity === 'Own House') pointsContribution += 6;
  else if (lordDignity === 'Debilitated') pointsContribution -= 6;

  pointsContribution += (beneficsInHouse.length * 3) - (maleficsInHouse.length * 2);

  let signalStrength: VargaSignal['signalStrength'] = 'NEUTRAL';
  if (pointsContribution >= 5) signalStrength = 'STRONG_POSITIVE';
  else if (pointsContribution >= 2) signalStrength = 'POSITIVE';
  else if (pointsContribution <= -4) signalStrength = 'CHALLENGED';
  else if (pointsContribution < 0) signalStrength = 'MIXED';

  let rationaleEn = `${vargaCode} shows House ${houseNumber} in ${houseSign} governed by ${houseLord} (${lordDignity}).`;
  let rationaleHi = `${vargaCode} में भाव ${houseNumber} ${houseSign} राशि में है, जिसके स्वामी ${houseLord} (${lordDignity}) हैं।`;

  if (signalStrength === 'STRONG_POSITIVE') {
    rationaleEn += ` Strong divisional confirmation provides elevated manifestation capacity.`;
    rationaleHi += ` मजबूत वर्गीय पुष्टि उत्कृष्ट फल प्रदायक क्षमता प्रदान करती है।`;
  } else if (signalStrength === 'CHALLENGED') {
    rationaleEn += ` Requires extra perseverance to overcome structural divisional resistance.`;
    rationaleHi += ` वर्गीय चुनौतियों को पार करने के लिए अतिरिक्त प्रयास और धैर्य आवश्यक है।`;
  }

  return {
    vargaCode,
    vargaNameEn: VARGA_NAMES_EN[vargaCode] || vargaCode,
    vargaNameHi: VARGA_NAMES_HI[vargaCode] || vargaCode,
    relevantHouse: houseNumber,
    houseSign,
    houseLord,
    lordDignity,
    occupantPlanets,
    signalStrength,
    pointsContribution,
    rationaleEn,
    rationaleHi
  };
}

export function analyzeCrossVargaConvergence(
  kundali: CompleteKundali,
  themeId: LifeThemeId = 'career_job',
  lang: Language = 'en'
): VargaConvergenceAnalysis {
  const isHi = lang === 'hi';
  const routing = THEME_VARGA_ROUTING[themeId] || THEME_VARGA_ROUTING.career_job;

  const signals: VargaSignal[] = [];
  const supportingSignals: string[] = [];
  const supportingSignalsHi: string[] = [];
  const conflictingSignals: string[] = [];
  const conflictingSignalsHi: string[] = [];
  const neutralSignals: string[] = [];
  const neutralSignalsHi: string[] = [];

  let totalPoints = 0;
  let maxPossiblePoints = routing.vargas.length * 8;

  routing.vargas.forEach(vargaCode => {
    const signal = evaluateVargaSignal(kundali, vargaCode, routing.primaryHouse, isHi);
    signals.push(signal);
    totalPoints += Math.max(-8, Math.min(8, signal.pointsContribution));

    if (signal.signalStrength === 'STRONG_POSITIVE' || signal.signalStrength === 'POSITIVE') {
      supportingSignals.push(`${signal.vargaCode}: ${signal.rationaleEn}`);
      supportingSignalsHi.push(`${signal.vargaCode}: ${signal.rationaleHi}`);
    } else if (signal.signalStrength === 'CHALLENGED' || signal.signalStrength === 'MIXED') {
      conflictingSignals.push(`${signal.vargaCode}: ${signal.rationaleEn}`);
      conflictingSignalsHi.push(`${signal.vargaCode}: ${signal.rationaleHi}`);
    } else {
      neutralSignals.push(`${signal.vargaCode}: ${signal.rationaleEn}`);
      neutralSignalsHi.push(`${signal.vargaCode}: ${signal.rationaleHi}`);
    }
  });

  // Calculate normalized Convergence Index: 0% to 100%
  // 0 points corresponds to 50% baseline
  const normalizedIndex = Math.round(50 + (totalPoints / (maxPossiblePoints || 1)) * 50);
  const convergenceIndex = Math.max(10, Math.min(95, normalizedIndex));

  let convergenceLevel: VargaConvergenceAnalysis['convergenceLevel'] = 'MODERATE';
  if (convergenceIndex >= 80) convergenceLevel = 'VERY_HIGH';
  else if (convergenceIndex >= 65) convergenceLevel = 'HIGH';
  else if (convergenceIndex < 35) convergenceLevel = 'DIVERGENT';
  else if (convergenceIndex < 50) convergenceLevel = 'LOW';

  let synthesisVerdictEn = `Cross-varga corroboration across ${routing.vargas.join(', ')} shows ${convergenceLevel.toLowerCase()} harmonic alignment (${convergenceIndex}%).`;
  let synthesisVerdictHi = `${routing.vargas.join(', ')} वर्गों में बहु-स्तरीय मिलान ${convergenceLevel === 'VERY_HIGH' || convergenceLevel === 'HIGH' ? 'अत्यंत सुदृढ़' : 'संतुलित'} समन्वय (${convergenceIndex}%) दर्शाता है।`;

  if (convergenceLevel === 'VERY_HIGH' || convergenceLevel === 'HIGH') {
    synthesisVerdictEn += ` Natal promise in D1 is deeply reinforced in divisional counterparts, maximizing real-world fruition.`;
    synthesisVerdictHi += ` D1 की जन्म क्षमता को संबंधित वर्ग कुंडलियों से पूर्ण समर्थन मिल रहा है, जिससे व्यावहारिक सफलता की संभावना उच्च है।`;
  } else if (convergenceLevel === 'DIVERGENT' || convergenceLevel === 'LOW') {
    synthesisVerdictEn += ` Divisional counterparts suggest mixed signals; tangible results require targeted remediation and focused effort.`;
    synthesisVerdictHi += ` वर्ग कुंडलियों में मिश्रित संकेत हैं; लक्ष्यों की प्राप्ति के लिए एकाग्र प्रयास और उपचारात्मक समर्थन आवश्यक है।`;
  }

  return {
    themeId,
    themeNameEn: routing.themeNameEn,
    themeNameHi: routing.themeNameHi,
    applicableVargas: routing.vargas,
    convergenceIndex,
    convergenceLevel,
    signals,
    supportingSignals: supportingSignals.length > 0 ? supportingSignals : ['Baseline foundational consistency across charts.'],
    supportingSignalsHi: supportingSignalsHi.length > 0 ? supportingSignalsHi : ['विभिन्न वर्गों में आधारभूत स्थिरता।'],
    conflictingSignals: conflictingSignals.length > 0 ? conflictingSignals : ['No major contradictory divisional afflictions.'],
    conflictingSignalsHi: conflictingSignalsHi.length > 0 ? conflictingSignalsHi : ['कोई गंभीर वर्गीय बाधा उपस्थित नहीं है।'],
    neutralSignals,
    neutralSignalsHi,
    synthesisVerdictEn,
    synthesisVerdictHi,
    confidence: routing.vargas.length >= 3 ? 'HIGH' : 'MEDIUM'
  };
}
